import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Zap, Clock, ToggleLeft, ToggleRight, Trash2, Settings, Calendar, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


export default function AutomationPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ name: '', trigger: '', action: '' });
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchAutomations = useCallback(async () => {
        try {
            const res = await fetch(`${API}/automations`, { headers });
            if (res.ok) setAutomations(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchAutomations(); }, [fetchAutomations]);

    useEffect(() => {
        if (searchParams.get('action') === 'nova_automacao') { setShowModal(true); setSearchParams({}); }
    }, [searchParams]);

    const handleSave = async () => {
        if (!form.name.trim() || !form.trigger.trim() || !form.action.trim()) { toast.error('Preencha todos os campos'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/automations`, { method: 'POST', headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success('Automação criada'); setShowModal(false); setForm({ name: '', trigger: '', action: '' }); fetchAutomations(); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const toggleStatus = async (id) => {
        const res = await fetch(`${API}/automations/${id}/status`, { method: 'PUT', headers });
        if (res.ok) { const d = await res.json(); setAutomations(p => p.map(a => a.automation_id === id ? { ...a, status: d.status } : a)); toast.success(`Status: ${d.status}`); }
    };

    const deleteAuto = async (id) => {
        if (!window.confirm('Excluir automação?')) return;
        const res = await fetch(`${API}/automations/${id}`, { method: 'DELETE', headers });
        if (res.ok) { setAutomations(p => p.filter(a => a.automation_id !== id)); toast.success('Automação excluída'); }
    };

    const active = automations.filter(a => a.status === 'active').length;

    return (
        <div className="sv-page" data-testid="automation-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Automações</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{active} ativas de {automations.length}</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => setShowModal(true)} data-testid="new-automation-btn"><Plus size={14} /> Nova Automação</button>
            </div>

            <div className="sv-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="sv-kpi"><span className="sv-kpi-label">Ativas</span><span className="sv-kpi-value">{active}</span><span className="sv-kpi-delta up">Rodando</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Execuções Total</span><span className="sv-kpi-value">{automations.reduce((a, c) => a + (c.runs || 0), 0)}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Pausadas</span><span className="sv-kpi-value">{automations.length - active}</span></div>
            </div>

            {loading ? (
                <div className="sv-card-system sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
            ) : automations.length === 0 ? (
                <div className="sv-card-system sv-empty-state">
                    <Zap /><p className="sv-empty-state-title">Nenhuma automação</p><p className="sv-empty-state-desc">Crie sua primeira automação para automatizar tarefas</p>
                    <button className="sv-btn-gold-system sv-btn-system mt-3" onClick={() => setShowModal(true)}><Plus size={14} /> Criar Automação</button>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {automations.map(auto => (
                        <div key={auto.automation_id} className="sv-card-system" data-testid={`automation-${auto.automation_id}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: auto.status === 'active' ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                        <Zap size={18} style={{ color: auto.status === 'active' ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm truncate" style={{ color: 'var(--text)' }}>{auto.name}</p>
                                        <div className="flex items-center gap-2 text-xs mt-1" style={{ color: 'var(--muted-2)' }}>
                                            <span>{auto.trigger}</span><span style={{ color: 'var(--border)' }}>→</span><span>{auto.action}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 ml-3">
                                    <div className="text-right hidden sm:block">
                                        <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{auto.runs || 0} execucoes</p>
                                        <p className="text-xs" style={{ color: 'var(--muted-2)' }}><Clock size={10} className="inline mr-1" />{auto.last_run || 'Nunca'}</p>
                                    </div>
                                    <button onClick={() => toggleStatus(auto.automation_id)} data-testid={`toggle-${auto.automation_id}`}>
                                        {auto.status === 'active' ? <ToggleRight size={28} style={{ color: 'var(--sv-gold-primary)' }} /> : <ToggleLeft size={28} style={{ color: 'var(--muted-2)' }} />}
                                    </button>
                                    <button className="sv-btn-system" style={{ padding: '4px 8px', color: '#ef4444' }} onClick={() => deleteAuto(auto.automation_id)}><Trash2 size={12} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="automation-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Nova Automação</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Alerta de preço baixo"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="automation-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Gatilho (Trigger)</label>
                                <input type="text" value={form.trigger} onChange={e => setForm({ ...form, trigger: e.target.value })} placeholder="Ex: Preço abaixo de R$50"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="automation-trigger-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Ação</label>
                                <input type="text" value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} placeholder="Ex: Enviar para Telegram"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="automation-action-input" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="automation-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Criar Automação
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
